import React, {
  useContext,
  type FC,
  type MouseEvent,
  type FocusEvent,
  useEffect,
  useRef,
  type PropsWithChildren,
} from 'react';

import { mqMin, theme, variant } from '@lucid/theme';
import { useSiteMetadata } from '@lucid/utils';
import { getLinkPath } from '@lucid/utils-drupal';
import gsap from 'gsap';
import styled, { type StyledComponent } from '@emotion/styled';

import type { CustomDrupal, Lucid } from '../../types/types';
import Box from '../box/box';
import LayoutContext from '../context/layout-context';
import Nav, { SUPER_NAV_HEIGHT, SITE_NAV_HEIGHT, type SharedNavOptions } from './nav';
import Text, { type TextProps } from '../text/text';
import NavDropdownMenuGroup from './nav-dropdown-menu-group';

export interface NavDropdownMenuProps extends SharedNavOptions {
  /** Allows the component to get additional styles. */
  className?: string;
  /** Checks if the dropdown should be visible or hidden. */
  isDropdownOpen?: boolean;
  /** The link and nested links to be shown in the dropdown menu. */
  link: CustomDrupal.LinkInterface;
  onBlur?: (event: FocusEvent) => void;
  onFocus?: (event: FocusEvent) => void;
  onMouseEnter?: (event: MouseEvent) => void;
  onMouseLeave?: (event: MouseEvent) => void;
}

type NavDropdownLinkGroups = (CustomDrupal.LinkInterface[] | null | undefined)[];

export interface NavDropdownMenuLinkInterface extends CustomDrupal.LinkInterface {
  /** Creates proper size groups for nested links to be shown in the dropdown menu. */
  linkGroups?: NavDropdownLinkGroups;
  /** Hides the group title if the link group is split into 2 or more groups. */
  shouldHideGroupTitle?: boolean;
}

const chunkLinks = (link: CustomDrupal.LinkInterface, links: CustomDrupal.LinkInterface[] = []) =>
  links.map((_, i) => (i % 8 === 0 ? link.links?.slice(i, i + 8) : null)).filter(Boolean);

const formatLinkGroups = (link: CustomDrupal.LinkInterface) => {
  const currentMenu = link.links?.reduce<NavDropdownMenuLinkInterface[]>((previous, item) => {
    // if the item has links (children) it means this item is a category and the children need to be chunked together into groups
    if (item.links && !!item.links.length) {
      const currentItems = chunkLinks(item, item.links);
      return [
        ...previous,
        {
          ...item,
          linkGroups: currentItems || [],
        },
      ];
    }
    // if the item has no links (children) it means it is part of a top level group and it needs to be chunked together with its siblings
    const currentItems = chunkLinks(link, link.links);
    const hasPreviousLink = previous.find(pItem => pItem.entityId === link.entityId);
    if (!hasPreviousLink) {
      const currentLinkGroupItems: NavDropdownMenuLinkInterface[] = currentItems.map(menuGroup => {
        if (menuGroup && !!menuGroup.length) {
          return {
            ...link,
            shouldHideGroupTitle: true,
            linkGroups: [...menuGroup],
          };
        }

        return {};
      });

      return [...previous, ...currentLinkGroupItems];
    }

    return previous;
  }, []);

  return currentMenu;
};
interface StyledTextContentProps extends TextProps {
  type: Lucid.Theme.Component.Nav['TextContent']['Variant'];
}

const StyledTextContent: StyledComponent<StyledTextContentProps> = styled(Text)(
  variant({
    scale: 'nav.textContent',
    prop: 'type',
    variants: {
      defaultVariants: {},
    },
  }),
);

const NavDropdownMenu: FC<PropsWithChildren<NavDropdownMenuProps>> = ({
  className,
  isDropdownOpen,
  isSuperNav = false,
  link,
  onBlur,
  onFocus,
  onMouseEnter,
  onMouseLeave,
  ...rest
}) => {
  const {
    language: { languageKey },
  } = useContext(LayoutContext);
  const { viewMoreText } = useContext(Nav.Context);
  const currentMenu = formatLinkGroups(link);
  const { product } = useSiteMetadata();

  const viewMoreLink = getLinkPath(link?.fieldViewMore?.url as CustomDrupal.Url, languageKey);

  const handleOnMouseEnter = (e: MouseEvent) => {
    if (onMouseEnter) {
      return onMouseEnter(e);
    }
  };

  const handleOnMouseLeave = (e: MouseEvent) => {
    if (onMouseLeave) {
      return onMouseLeave(e);
    }
  };

  const handleOnBlur = (e: FocusEvent) => {
    if (onBlur && !e.currentTarget.contains(e.relatedTarget as Node)) {
      // does not blur if focus stays within the same dropdown
      return onBlur(e);
    }
  };

  const handleOnFocus = (e: FocusEvent) => {
    if (onFocus) {
      return onFocus(e);
    }
  };

  const animationRef = useRef<HTMLDivElement>(null);
  const menuLinksRef = useRef<HTMLDivElement>(null);
  const featuredItemRef = useRef<HTMLDivElement>(null);
  const containerQ = gsap.utils.selector(animationRef);
  const menuQ = gsap.utils.selector(menuLinksRef);
  const featuredQ = featuredItemRef.current ? gsap.utils.selector(featuredItemRef) : () => null;
  gsap.config({ nullTargetWarn: false });

  useEffect(() => {
    if (animationRef.current) {
      isDropdownOpen
        ? gsap
            .timeline()
            .to(
              menuQ('a'),
              {
                opacity: 1,
                duration: 0.1,
                stagger: { each: 0.025 },
                ease: theme.beziers.slowStartEase,
              },
              '0',
            )
            .to(
              containerQ('[aria-level="3"]'),
              {
                opacity: 1,
                duration: 0.5,
                overwrite: true,
              },
              '0',
            )
            .to(
              menuQ('a'),
              {
                y: 0,
                duration: 0.157,
                stagger: { each: 0.025 },
                ease: theme.beziers.slowStartEase,
              },
              '0',
            )
            .to(
              featuredItemRef.current,
              {
                opacity: 1,
                duration: 0.25,
              },
              '0.2',
            )
            .to(
              featuredQ('p'),
              {
                x: 0,
                duration: 0.3,
              },
              '0.2',
            )
        : gsap
            .timeline()
            .set(featuredItemRef.current, {
              opacity: 0,
              overwrite: true,
            })
            .set(containerQ('[aria-level="3"]'), {
              opacity: 0,
              overwrite: true,
            })
            .set(menuQ('a'), {
              opacity: 0,
              y: '-16px',
              overwrite: true,
            })
            .set(featuredQ('p'), {
              x: '-16px',
              overwrite: true,
            });
    }
  }, [isDropdownOpen]);

  return (
    <Box
      ref={animationRef}
      {...rest}
      aria-hidden={!isDropdownOpen}
      css={theme => [
        {
          label: 'navDropdownMenu',
          display: 'flex',
          flexDirection: 'column',
          flexWrap: 'nowrap',
          backgroundColor: theme.colors.white,
          height: '100%',
          maxHeight: 0,
          width: '100%',
          overflow: 'hidden',
          [mqMin.tabletSmall]: {
            flexDirection: 'row',
            flexWrap: 'wrap',
          },
          [mqMin.tabletLandscape]: {
            position: 'absolute',
            top: isSuperNav ? SUPER_NAV_HEIGHT : SITE_NAV_HEIGHT,
            padding: 0,
            flexDirection: 'row',
            flexWrap: 'nowrap',
            height: 'auto',
            left: 0,
            margin: '0 3.75rem',
            width: 'fit-content',
          },
          [mqMin.computer]: {
            width: 'fit-content',
            left: '15%',
          },
          [mqMin.maxWidth]: {
            margin: '0 7.5rem',
          },
        },
        isDropdownOpen && {
          zIndex: theme.zIndices.z100,
          maxHeight: '1000px',
          [mqMin.tabletLandscape]: {
            marginTop: 'unset',
            paddingBottom: 'unset',
            boxShadow: theme.shadows.nav,
            borderBottomLeftRadius: theme.radii.textOnly,
            borderBottomRightRadius: theme.radii.textOnly,
          },
        },
      ]}
      // eslint-disable-next-line react/jsx-sort-props
      className={className}
      onBlur={handleOnBlur}
      onFocus={handleOnFocus}
      onMouseEnter={handleOnMouseEnter}
      onMouseLeave={handleOnMouseLeave}
    >
      <StyledTextContent
        css={theme => ({
          label: 'navDropdownMenuTextContent',
          display: 'none',
          visibility: 'hidden',
          position: 'relative',
          [mqMin.tabletLandscape]: {
            display: 'flex',
            visibility: 'visible',
            flexDirection: 'column',
            flex: '0 1 100%',
            width: '250px',
            padding: theme.space.s24,
            color: theme.colors.white,
          },
        })}
        type={isSuperNav ? 'superNav' : 'siteNav'}
      >
        <Text as="span" dataElementPageLocation={isSuperNav ? 'superNav' : 'siteNav'} variant="paragraphL400">
          {link.fieldDropdownDescription}
        </Text>
      </StyledTextContent>
      <Box
        ref={menuLinksRef}
        css={theme => [
          {
            label: 'navDropdownMenuLinks',
            position: 'relative',
            alignSelf: 'flex-start',
            width: '100%',
            padding: `${theme.space.s16}px ${theme.space.s32}px 0`,
            [mqMin.tabletLandscape]: {
              display: 'flex',
              padding: theme.space.s24,
              gap: theme.space.s24,
            },
            [mqMin.computer]: {
              width: 'unset',
            },
          },
          isDropdownOpen && {
            zIndex: theme.zIndices.z500,
          },
        ]}
      >
        {currentMenu?.map((itemLink, itemLinkIndex, menuArr) => {
          const isNestedLinkGroups =
            itemLink.linkGroups &&
            !!itemLink.linkGroups.length &&
            itemLink.linkGroups.length > 1 &&
            itemLink.linkGroups.map(linkGroup => Array.isArray(linkGroup)).some(Boolean);

          const isLastColumn = menuArr.length === itemLinkIndex + 1;
          const shouldShowViewMore = isLastColumn && !!viewMoreLink;

          if (isNestedLinkGroups) {
            return itemLink.linkGroups?.map((linkGroup, linkGroupIndex) => {
              const isGroupSplit = linkGroupIndex !== 0;
              const nestedLink = {
                ...itemLink,
                linkGroups: linkGroup,
                shouldHideGroupTitle: isGroupSplit,
              } as NavDropdownMenuLinkInterface;

              return (
                <NavDropdownMenuGroup
                  key={`navDropdownMenuGroup-${link.entityId}-${linkGroupIndex + 1}`}
                  index={linkGroupIndex}
                  isDropdownOpen={isDropdownOpen}
                  isGroupSplit={isGroupSplit}
                  isSuperNav={isSuperNav}
                  link={nestedLink}
                  product={product}
                  viewMoreLink={shouldShowViewMore && isGroupSplit ? viewMoreLink : undefined}
                  viewMoreText={viewMoreText}
                />
              );
            });
          }

          return (
            <NavDropdownMenuGroup
              key={`navDropdownMenuGroup-${link.entityId}-${itemLinkIndex + 1}`}
              index={itemLinkIndex}
              isDropdownOpen={isDropdownOpen}
              isSuperNav={isSuperNav}
              link={itemLink}
              product={product}
              viewMoreLink={shouldShowViewMore ? viewMoreLink : undefined}
              viewMoreText={viewMoreText}
            />
          );
        })}
      </Box>
    </Box>
  );
};

export { NavDropdownMenu };
export default NavDropdownMenu;
